// elements/theming/coloring-system.ts
import { ASS_PRIMITIVE } from "@adornis/ass/index.js";
import { LocalStorageReactive } from "@adornis/base/rxjs-extension/reactives/client/local-storage.js";
import { BehaviorSubject } from "rxjs";
import {
  rawToHex,
  rgbLogShade,
  textColorFromBackgroundColor,
  toRawColor
} from "./color-shades.js";
var _a;
var Color = class _Color {
  /**
   * use Color.make(...)
   */
  constructor(color, on = textColorFromBackgroundColor) {
    this[_a] = true;
    this.raw = color;
    this.string = rawToHex(color);
    this._on = on;
  }
  static {
    _a = ASS_PRIMITIVE;
  }
  static {
    this.white = new _Color({ r: 1, g: 1, b: 1, a: 1 });
  }
  static {
    this.black = new _Color({ r: 0, g: 0, b: 0, a: 1 });
  }
  shade(shade) {
    switch (shade) {
      case 0:
        return new _Color(rgbLogShade(this.raw, 1), this._on);
      case 50:
        return new _Color(rgbLogShade(this.raw, 0.8), this._on);
      case 100:
        return new _Color(rgbLogShade(this.raw, 0.6), this._on);
      case 200:
        return new _Color(rgbLogShade(this.raw, 0.4), this._on);
      case 300:
        return new _Color(rgbLogShade(this.raw, 0.2), this._on);
      case 400:
        return this;
      case 500:
        return new _Color(rgbLogShade(this.raw, -0.2), this._on);
      case 600:
        return new _Color(rgbLogShade(this.raw, -0.4), this._on);
      case 700:
        return new _Color(rgbLogShade(this.raw, -0.6), this._on);
      case 800:
        return new _Color(rgbLogShade(this.raw, -0.8), this._on);
      case 900:
        return new _Color(rgbLogShade(this.raw, -0.95), this._on);
      case 1e3:
        return new _Color(rgbLogShade(this.raw, -1), this._on);
      default:
        throw new Error(`dont support shade ${shade}`);
    }
  }
  on(lightColor, darkColor) {
    return new _Color(this._on(this.raw, lightColor, darkColor), this._on);
  }
  opacity(op) {
    const raw = { ...this.raw };
    raw.a = op;
    return new _Color(raw, this._on);
  }
  toString() {
    return this.string;
  }
  static make(color, on) {
    return new _Color(
      typeof color === "string" ? toRawColor(color) : { ...color.raw },
      on ?? (typeof color !== "string" ? color._on : void 0)
    );
  }
};
var ColorScheme = class {
  constructor(scheme) {
    this.primary = Color.make(scheme.primary);
    this.secondary = Color.make(scheme.secondary);
    this.accent = Color.make(scheme.accent);
    this.neutral = Color.make(scheme.neutral);
    this.dark = this.neutral.shade(900);
    this.light = this.neutral.shade(50);
    this.black = Color.black;
    this.white = Color.white;
    this.tone = Object.fromEntries(Object.entries(scheme.tone).map(([key, value]) => [key, Color.make(value)]));
  }
  get on() {
    return this._onProxy ??= new Proxy(this, {
      get: (target, key) => {
        if (key === "tone") {
          return target._toneProxy ??= new Proxy(target.tone, {
            get: (toneTarget, key2) => {
              const value2 = toneTarget[key2];
              if (!value2)
                return void 0;
              const patched2 = patchOn(value2, target);
              return patched2.on(target.white.raw, target.black.raw);
            }
          });
        }
        const value = target[key];
        if (!value)
          return void 0;
        const patched = patchOn(value, target);
        return patched.on(target.white.raw, target.black.raw);
      },
      set: () => {
        throw new Error("updating colors is not supported atm, could be added later");
      }
    });
  }
};
var patchedColor = Symbol("patchedColor");
var patchOn = (value, target) => {
  if (value[patchedColor])
    return value[patchedColor];
  const patched = Color.make(value);
  patched.on = (light, dark) => {
    const onColor = value.on(light, dark);
    onColor.shade = (shade) => value.shade(shade).on(light, dark);
    return onColor;
  };
  value[patchedColor] = patched;
  return patched;
};
var theme = LocalStorageReactive ? new LocalStorageReactive("__adornis_theme", "light") : new BehaviorSubject("light");
export {
  Color,
  ColorScheme,
  theme
};
